import { CheckCheck, History, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { href } from '../../lib/router';
import { useToast } from '../../ui/Toasts';
import { useCarried, useConfirmCarried } from './api';

/**
 * A renewal check's answers from last time (task 69): the business confirms they are still true in one
 * go, one by one, or changes the ones that changed in the form.
 */
export function CarriedCard({ assessmentId, canAnswer }: { assessmentId: string; canAnswer: boolean }) {
  const carried = useCarried(assessmentId);
  const confirm = useConfirmCarried(assessmentId);
  const toast = useToast();
  const [showAll, setShowAll] = useState(false);
  const items = carried.data ?? [];
  if (!items.length) return null;
  const open = items.filter((c) => c.state === 'open');
  const shown = showAll ? open : open.slice(0, 6);
  const settled = items.length - open.length;

  const run = async (questions?: string[]) => {
    try {
      const r = await confirm.mutateAsync(questions);
      toast(
        r.waiting.length
          ? `${r.confirmed.length} confirmed. ${r.waiting.length} can be confirmed once their part of the check opens.`
          : `${r.confirmed.length} answer${r.confirmed.length === 1 ? '' : 's'} confirmed.`,
      );
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not confirm', 'bad');
    }
  };

  return (
    <section className="card pad carried-card" aria-labelledby="carried-title">
      <div className="row-between">
        <h2 id="carried-title" className="section-title"><History size={16} aria-hidden /> Still true?</h2>
        <span className="muted small">{settled} of {items.length} checked</span>
      </div>
      {open.length ? (
        <>
          <p className="muted small">Your answers from {items[0]!.from.title}. Confirm the ones that are still true; change the rest in the form.</p>
          <ul className="carried-list">
            {shown.map((c) => (
              <li key={c.question}>
                <div className="minw">
                  <div className="small">{c.text}</div>
                  <b>{c.answer}</b>
                </div>
                {canAnswer && (
                  <span className="nowrap">
                    <button type="button" className="btn btn-sm" disabled={confirm.isPending} onClick={() => run([c.question])}>Still true</button>
                    <a className="btn btn-sm btn-ghost" href={href.answer(assessmentId, c.question)}>Change</a>
                  </span>
                )}
              </li>
            ))}
          </ul>
          {open.length > shown.length && <button type="button" className="linklike left small" onClick={() => setShowAll(true)}>Show all {open.length}</button>}
          {canAnswer && (
            <button type="button" className="btn btn-primary" disabled={confirm.isPending} onClick={() => run()}>
              {confirm.isPending ? <Loader2 size={15} className="spin" /> : <CheckCheck size={15} />} All {open.length} still true
            </button>
          )}
        </>
      ) : (
        <p className="muted small">Every answer from {items[0]!.from.title} has been confirmed or changed.</p>
      )}
    </section>
  ); // prettier-ignore
}
